import React, { useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Avatar, IconButton } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import VuiTypography from "components/VuiTypography";
import VuiBox from "components/VuiBox";
import { useTranslation } from "react-i18next";

// Helper to format time as 'HH:mm'
function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(":");
  return `${h}:${m}`;
}

// Helper to get unique days from all schedules
function getUniqueDays(schedules) {
  const allDates = schedules.map(s => s.date);
  const set = new Set(allDates);
  return Array.from(set).sort();
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const CAR_DETAIL_WIDTH = 200; // px, Adjust as needed for car details
const DRIVER_DETAIL_WIDTH = 150; // px, Adjust as needed for driver details
const HOUR_WIDTH = 60; // px, Reduced from 60 to 40

const GanttTimeline = ({ groupedSchedules = {}, conflictingSchedules = [], onDeleteSchedule }) => {
  const { t } = useTranslation();
  const allSchedules = useMemo(() => Object.values(groupedSchedules).flatMap(group => group.schedules), [groupedSchedules]);
  const timelineRef = useRef(null); // Ref for the timeline container
  const scheduleRefs = useRef({}); // Refs for individual schedule elements

  useEffect(() => {
    // Clear old refs
    scheduleRefs.current = {};
  }, [groupedSchedules]); // Clear refs when schedules change

  useEffect(() => {
    if (conflictingSchedules && conflictingSchedules.length > 0) {
      // Find the first conflicting schedule's ID
      const firstConflictId = conflictingSchedules[0].id;
      // Find the corresponding DOM element using the stored ref
      const conflictElement = scheduleRefs.current[firstConflictId];

      if (conflictElement) {
        // Scroll the element into view, centered horizontally
        conflictElement.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest'
        });
      }
    }
  }, [conflictingSchedules]); // Run effect when conflictingSchedules change

  if (!allSchedules.length) {
    return (
      <VuiBox p={2}>
        <VuiTypography variant="caption" color="text">
          {t('shedules.not_found')}
        </VuiTypography>
      </VuiBox>
    );
  }

  console.log('GanttTimeline received groupedSchedules:', groupedSchedules);
  console.log('GanttTimeline received conflictingSchedules:', conflictingSchedules);
  
  const days = useMemo(() => getUniqueDays(allSchedules), [allSchedules]);

  // Calculate total timeline width based on number of days and hours
  const totalTimelineWidth = days.length * 24 * HOUR_WIDTH;
  const carIds = useMemo(() => Object.keys(groupedSchedules), [groupedSchedules]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header: Days and Hours */}
      <Box sx={{ overflowX: "auto" }} ref={timelineRef}>
        <Box sx={{ 
          minWidth: totalTimelineWidth + 40, // Adjusted minWidth
          p: 1, 
          display: "flex", 
          flexDirection: "column",
          background: "rgba(255,255,255,0.05)",
          borderRadius: "8px",
        }}>
          {/* Timeline Header */}
          <Box>
            {/* Day Headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${days.length}, ${24 * HOUR_WIDTH}px)`,
                borderBottom: "1px solid rgba(255,255,255,0.07)",
                height: 28, 
                alignItems: 'center',
                background: "linear-gradient(90deg, #22223a 60%, #1a1a2e 100%)",
                borderRadius: "8px 8px 0 0",
              }}
            >
              {days.map(day => (
                <Box key={day} sx={{ textAlign: "center" }}>
                  <VuiTypography variant="caption" color="white" fontWeight="medium" sx={{ fontSize: '11px' }}>
                    {day}
                  </VuiTypography>
                </Box>
              ))}
            </Box>
            {/* Hour Headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${days.length * 24}, ${HOUR_WIDTH}px)`,
                borderBottom: "1px solid rgba(255,255,255,0.07)",
                mb: 1,
                height: 28, 
                background: "rgba(25,25,50,0.85)",
              }}
            >
              {days.flatMap(day => (
                HOURS.map(h => (
                  <Box key={`${day}-${h}`} sx={{ 
                    textAlign: "center", 
                    borderRight: "1px solid rgba(255,255,255,0.07)",
                    '&:last-child': {
                      borderRight: 'none'
                    }
                  }}>
                    <VuiTypography variant="caption" color="text" sx={{ fontSize: '10px' }}>
                      {h.toString().padStart(2, '0')}
                    </VuiTypography>
                  </Box>
                ))
              ))}
            </Box>
          </Box>

          {/* Schedules as rows (grouped by car) */}
          <Box sx={{ position: "relative", height: carIds.length * 80, background: "rgba(25,25,50,0.85)", borderRadius: "0 0 8px 8px", p: 1 }}>
            {carIds.map((carId, carIndex) => {
              const carGroup = groupedSchedules[carId];
              const car = carGroup.car;

              return (
                <VuiBox key={carId} sx={{ position: "absolute", top: carIndex * 80 + 5, left: 0, right: 0, height: 80, display: "flex", alignItems: "center", borderBottom: '1px dashed rgba(255,255,255,0.05)' }}>
                  {/* Car Details on the left - REMOVED */}

                  {/* Schedules for this car on the timeline */}
                  <Box sx={{ position: "relative", flexGrow: 1, height: "100%" }}>
                    {carGroup.schedules.map((s, scheduleIndex) => {
                      const dayIndex = days.indexOf(s.date);
                      if (dayIndex === -1) return null;

                      const [startH, startM] = s.start_time.split(":").map(Number);
                      const [endH, endM] = s.end_time.split(":").map(Number);

                      const startHourOffset = startH + startM / 60;
                      const endHourOffset = endH + endM / 60;

                      const left = (dayIndex * 24 * HOUR_WIDTH) + (startHourOffset * HOUR_WIDTH);
                      let width = (endHourOffset - startHourOffset) * HOUR_WIDTH;
                      if (width < 0) width = 0;

                      const driverFirstName = s.driver?.user?.first_name || '';
                      const driverLastName = s.driver?.user?.last_name || '';
                      const driverFullName = driverFirstName || driverLastName ? `${driverFirstName} ${driverLastName}`.trim() : 'No Driver';
                      const startTime = formatTime(s.start_time);
                      const endTime = formatTime(s.end_time);

                      const isConflicting = conflictingSchedules.some(conflict => conflict.id === s.id);

                      console.log('Schedule ID:', s.id, 'Conflicting Schedules:', conflictingSchedules);

                      return (
                        <Box
                          key={`${s.id || scheduleIndex}-${s.date}`}
                          ref={(el) => {
                            if (s.id) {
                              scheduleRefs.current[s.id] = el;
                            }
                          }}
                          sx={{
                            position: "absolute",
                            top: '50%', 
                            transform: 'translateY(-50%)',
                            left,
                            width: Math.max(width, 90), 
                            height: 70,
                            background: "linear-gradient(90deg, #43a047 0%, #388e3c 100%)",
                            borderRadius: 6,
                            display: "flex",
                            flexDirection: 'column',
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontWeight: 400,
                            fontSize: 11, 
                            padding: '0 6px', 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                            textAlign: 'center',
                            boxShadow: '0 2px 8px 0 rgba(67,160,71,0.10)',
                            border: '1px solid rgba(67,160,71,0.3)',
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: '0 4px 16px 0 rgba(67,160,71,0.18)',
                              transform: 'translateY(-50%) scale(1.02)',
                              '& .delete-icon': {
                                opacity: 1,
                              }
                            },
                            zIndex: 2,
                            ...(isConflicting && {
                              border: '2px solid red',
                              boxShadow: '0 0 10px red',
                            }),
                          }}
                          title={`Date: ${s.date}\nTime: ${startTime} - ${endTime}\nCar: ${car?.manufacturer || 'Unknown Manufacturer'} ${car?.model || 'Unknown Model'} (${car?.plate_number || 'N/A'})\nDriver: ${driverFullName}`}
                        >
                          {/* Delete Icon */}
                          {s.id && (
                            <IconButton
                              className="delete-icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteSchedule(s.id);
                              }}
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                padding: '2px',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'rgba(255,255,255,0.1)',
                                }
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}

                          {/* Car Image and Details - inside the bar */}
                          <VuiBox sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 0.3 }}>
                            {car?.images?.[0] && (
                              <Avatar 
                                src={car.images[0]} 
                                alt="car" 
                                sx={{ 
                                  width: 24, 
                                  height: 24, 
                                  mr: 0.6, 
                                  border: '1px solid rgba(255,255,255,0.2)'
                                }} 
                              />
                            )}
                            <VuiTypography variant="caption" color="white" sx={{ fontSize: '10px', fontWeight: 'inherit', lineHeight: 1.1 }}>
                              {`${car?.manufacturer || 'Unknown Manufacturer'} ${car?.model || 'Unknown Model'}`}
                            </VuiTypography>
                          </VuiBox>

                           {/* Driver Image and Name - inside the bar */}
                           <VuiBox sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 0.3 }}>
                            {s.driver?.profile_photos?.[0] && (
                              <Avatar 
                                src={s.driver.profile_photos[0]} 
                                alt="driver" 
                                sx={{ 
                                  width: 24, 
                                  height: 24, 
                                  mr: 0.6, 
                                  border: '1px solid rgba(255,255,255,0.2)'
                                }} 
                              />
                            )}
                            <VuiTypography variant="caption" color="white" sx={{ fontSize: '10px', fontWeight: 'inherit', lineHeight: 1.1 }}>
                              {driverFullName}
                            </VuiTypography>
                          </VuiBox>

                          {/* Time inside the bar */}
                          <VuiTypography variant="caption" color="white" sx={{ fontSize: '10px', fontWeight: 'inherit', lineHeight: 1.1 }}>
                            {`${startTime} - ${endTime}`}
                          </VuiTypography>
                        </Box>
                      );
                    })}
                  </Box>
                </VuiBox>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

GanttTimeline.propTypes = {
  groupedSchedules: PropTypes.objectOf(
    PropTypes.shape({
      car: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        images: PropTypes.arrayOf(PropTypes.string),
        model: PropTypes.string,
        manufacturer: PropTypes.string,
        plate_number: PropTypes.string,
      }),
      schedules: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          date: PropTypes.string.isRequired,
          start_time: PropTypes.string.isRequired,
          end_time: PropTypes.string.isRequired,
          driver: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            profile_photos: PropTypes.arrayOf(PropTypes.string),
            user: PropTypes.shape({
              id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
              first_name: PropTypes.string,
              last_name: PropTypes.string,
              is_driver: PropTypes.bool,
              rating_count: PropTypes.number,
              rating: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
            })
          }),
        })
      ).isRequired,
    })
  ).isRequired,
  conflictingSchedules: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ),
  onDeleteSchedule: PropTypes.func.isRequired,
};

export default GanttTimeline; 